import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { Headquarter } from '@repositories/models';

import { HEADQUARTERS } from './headquarters.data';

/**
 * Lugares donde se puede plantar un edificio, en orden de preferencia: primero
 * el anillo interior alrededor del hub (que es el reparto del diseño) y luego
 * los bordes del lienzo. Al crear un headquarter se le asigna el primer lugar
 * libre, así el alta no tiene que pedir coordenadas.
 */
const PLACEMENT_SLOTS: { x: number; y: number }[] = [
  // Anillo interior
  { x: 1200, y: 320 },
  { x: 820, y: 380 },
  { x: 1580, y: 380 },
  { x: 520, y: 640 },
  { x: 1780, y: 600 },
  { x: 720, y: 830 },
  { x: 1010, y: 890 },
  { x: 1340, y: 900 },
  { x: 1650, y: 830 },
  // Anillo exterior
  { x: 1200, y: 190 },
  { x: 640, y: 250 },
  { x: 1760, y: 250 },
  { x: 330, y: 430 },
  { x: 2070, y: 430 },
  { x: 300, y: 880 },
  { x: 2090, y: 880 },
  { x: 450, y: 960 },
  { x: 1930, y: 960 },
];

/** Distancia mínima entre dos edificios para que no se pisen */
const MIN_SLOT_DISTANCE = 210;

@Injectable({ providedIn: 'root' })
export class HeadquartersService {
  private readonly baseUrl = `${environment.repositoriesApiUrl}/api/headquarters`;

  /**
   * Mientras el back no exponga el endpoint, las consultas se sirven desde
   * `headquarters.data.ts`. Cuando exista, poner esta bandera en `false` y
   * las llamadas HTTP de abajo —que ya son las definitivas— pasan a usarse
   * sin tocar a los componentes; después se puede borrar la bandera y el
   * archivo de data.
   *
   * Mientras está encendida, las altas y bajas se aplican sobre la constante
   * en memoria: sirven para probar la pantalla, pero se pierden al recargar.
   */
  private readonly useTemporaryData = false;

  constructor(private _http: HttpClient) {}

  /**
   * Todos los headquarters con sus áreas.
   */
  getAll(): Observable<Headquarter[]> {
    if (this.useTemporaryData) return of(HEADQUARTERS);

    return this._http.get<Headquarter[]>(this.baseUrl).pipe(
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Un headquarter puntual por su id.
   */
  getById(id: number): Observable<Headquarter> {
    if (this.useTemporaryData) return of(HEADQUARTERS.find(hq => hq.id === id));

    return this._http.get<Headquarter>(`${this.baseUrl}/${id}`).pipe(
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Cantidad de headquarters activos. Es su propio endpoint para no traerse
   * el árbol completo cuando sólo se quiere el número.
   */
  count(): Observable<number> {
    if (this.useTemporaryData) return of(HEADQUARTERS.length);

    return this._http.get<any>(`${this.baseUrl}/count`).pipe(
      // El back puede responder el número pelado o envuelto en un objeto
      map(response => (typeof response === 'number' ? response : response?.count ?? 0)),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Crea un headquarter. Si no trae posición ni medidas, se las asigna solo:
   * el alta sólo pide los datos del sitio.
   */
  create(headquarter: Headquarter): Observable<Headquarter> {
    const toCreate = this.withPlacement(headquarter, HEADQUARTERS);

    if (this.useTemporaryData) {
      HEADQUARTERS.push(toCreate);
      return of(toCreate);
    }

    return this._http.post<Headquarter>(this.baseUrl, toCreate).pipe(
      map((response: any) => (response?.headquarter ?? response) as Headquarter),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Edita un headquarter existente.
   */
  update(headquarter: Headquarter): Observable<Headquarter> {
    if (this.useTemporaryData) {
      const index = HEADQUARTERS.findIndex(hq => hq.id === headquarter.id);
      if (index >= 0) HEADQUARTERS[index] = headquarter;
      return of(headquarter);
    }

    return this._http.put<Headquarter>(`${this.baseUrl}/${headquarter.id}`, headquarter).pipe(
      map((response: any) => (response?.headquarter ?? response) as Headquarter),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Completa el headquarter con su lugar en la ciudad y las medidas del
   * edificio, si no los trae. Se resuelve acá y no en el formulario para que
   * cualquier alta —esta pantalla o una futura— use la misma regla.
   */
  private withPlacement(headquarter: Headquarter, existing: Headquarter[]): Headquarter {
    if (headquarter.x && headquarter.y) return headquarter;

    const slot = this.findFreeSlot(existing);
    const seed = this.hash(headquarter.name);

    return {
      ...headquarter,
      x: slot.x,
      y: slot.y,
      // Las medidas varían con el nombre para que la ciudad no quede uniforme
      width: 88 + (seed % 4) * 8,
      height: 118 + (seed % 6) * 10,
      pin: 48,
    };
  }

  /**
   * Primer lugar libre de la lista. Si están todos ocupados, se abre camino
   * hacia afuera en espiral para que un alta nunca quede sin lugar.
   */
  private findFreeSlot(existing: Headquarter[]): { x: number; y: number } {
    const free = PLACEMENT_SLOTS.find(slot =>
      existing.every(hq => Math.hypot(hq.x - slot.x, hq.y - slot.y) >= MIN_SLOT_DISTANCE)
    );
    if (free) return free;

    // Ángulo áureo: reparte los excedentes sin que se alineen
    const extra = existing.length - PLACEMENT_SLOTS.length;
    const angle = extra * 2.39996;
    const radius = 1000 + Math.floor(extra / 8) * 120;

    return {
      x: Math.round(1200 + Math.cos(angle) * radius),
      y: Math.round(520 + Math.sin(angle) * radius * 0.45),
    };
  }

  /** Hash estable para derivar medidas a partir de un texto */
  private hash(value: string): number {
    let acc = 0;
    for (let i = 0; i < value.length; i++) {
      acc = (acc * 31 + value.charCodeAt(i)) % 100000;
    }
    return acc;
  }

  /**
   * Elimina un headquarter por su id.
   */
  delete(id: number): Observable<Headquarter> {
    if (this.useTemporaryData) {
      const index = HEADQUARTERS.findIndex(hq => hq.id === id);
      const [removed] = index >= 0 ? HEADQUARTERS.splice(index, 1) : [null];
      return of(removed);
    }

    return this._http.delete<Headquarter>(`${this.baseUrl}/${id}`).pipe(
      map((response: any) => (response?.headquarter ?? response) as Headquarter),
      catchError(e => {
        return throwError(e);
      })
    );
  }
}
